/**
 * Modal for viewing document upload screenshots with badge status indicators.
 * Reads its content from Redux modal metadata and supports an optional
 * `onClose` callback for cleanup (e.g. aborting in-flight requests).
 * @module documentScreenshotViewerModal
 */

import { Modal } from "@risalabs_frontend_org/oasis-ui-kit";
import { useDispatch, useSelector } from "react-redux";
import { ModalId } from "../../../enums/modalId";
import { closeModal } from "../../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../../redux/store/store";
import ImageViewerWithTextAndBadge, {
  ImageViewerWithTextAndBadgeProps,
} from "../../imageViewerWithTextAndBadge/imageViewerWithTextAndBadge";
import { LoaderMessage } from "../../loaderMessage/loaderMessage";
import NoData from "../../noData/noData";

/** Shape of the Redux `metaData` slice used by this modal. */
interface DocumentScreenshotViewerModalMetaData {
  title?: string;
  showLoading?: boolean;
  imagesWithBadges?: ImageViewerWithTextAndBadgeProps[];
}

/** Props for the {@link DocumentScreenshotViewerModal} component. */
interface DocumentScreenshotViewerModalProps {
  /** Override title shown in the modal header. */
  title?: string;
  /** Override for the loading indicator (metadata value takes precedence). */
  showLoading?: boolean;
  /** Optional callback invoked when the modal is closed or cancelled. */
  onClose?: () => void;
}

const DocumentScreenshotViewerModal = (
  props: DocumentScreenshotViewerModalProps,
) => {
  const dispatch = useDispatch<AppDispatch>();
  const { metaData } = useSelector((state: RootState) => state.modal);

  const modalData = metaData as
    | DocumentScreenshotViewerModalMetaData
    | undefined;
  const title =
    modalData?.title ?? props?.title ?? "Document Screenshot Viewer";
  const showLoading = modalData?.showLoading ?? props?.showLoading ?? false;
  const imagesWithBadges = modalData?.imagesWithBadges ?? [];

  return (
    <Modal
      dialogId={ModalId.DOCUMENT_SCREENSHOT_VIEWER_MODAL}
      onSave={() => {}}
      title={title}
      saveButtonText={"Save"}
      cancelText={"Cancel"}
      hideFooter={true}
      heightPercentage={80}
      onCancel={() => {
        dispatch(closeModal());
        props.onClose?.();
      }}
      onClose={() => {
        dispatch(closeModal());
        props.onClose?.();
      }}
    >
      <div className="image-viewer-with-badges-modal--container flex h-full flex-col overflow-hidden">
        {showLoading ? (
          <div className="loader-container flex h-full w-full items-center justify-center">
            <LoaderMessage message="Loading data..." />
          </div>
        ) : (
          <div className="image-viewer-with-badges-modal--container--content flex flex-1 flex-col gap-2 overflow-auto">
            {imagesWithBadges && imagesWithBadges.length > 0 ? (
              imagesWithBadges.map((imageWithBadge, index) => (
                <ImageViewerWithTextAndBadge
                  imagePath={imageWithBadge.imagePath}
                  badgeDetails={imageWithBadge.badgeDetails}
                  shouldShowBottomBorder={index !== imagesWithBadges.length - 1}
                  key={index + "_" + (imageWithBadge.imagePath ?? "")}
                  updatedAt={imageWithBadge.updatedAt}
                  text={imageWithBadge.text}
                />
              ))
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <NoData text="No screenshot found" />
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default DocumentScreenshotViewerModal;
